import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { FileText, Loader2, Package, Send } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  pharmaVendorPortalApi,
  type PharmacyPurchaseOrder,
  type VendorDeliveryLineInput,
} from "@/api/pharmaVendorPortal";
import type { PurchaseOrderLine } from "@/api/pharmacyPurchase";

type Drug = { id: string; name: string; strength?: string; form?: string; gstRate?: number | null };

function drugLabel(d?: Drug | null) {
  if (!d) return "—";
  return [d.name, d.strength, d.form].filter(Boolean).join(" · ");
}

/** Hospital workflow status — not “material shipped”. */
const VENDOR_PO_STATUS: Record<string, string> = {
  SENT: "PO received — enter invoice & batch",
  VENDOR_SUBMITTED: "Submitted — awaiting hospital",
  PARTIALLY_ACCEPTED: "Part received by hospital",
  ACCEPTED: "Fully received by hospital",
  REJECTED: "Rejected — resubmit",
};

function vendorPoStatusLabel(status: string) {
  return VENDOR_PO_STATUS[status] ?? status.replace(/_/g, " ");
}

type LineForm = VendorDeliveryLineInput & { lineKey: string; orderedQty?: number };

function emptyLine(drugId = "", orderedQty?: number): LineForm {
  return {
    lineKey: Math.random().toString(36).slice(2),
    drugId,
    orderedQty,
    batchNo: "",
    expiryDate: "",
    quantity: orderedQty && orderedQty > 0 ? orderedQty : 1,
    mrp: 0,
    purchaseRate: 0,
    gstRate: null,
    discountRate: null,
    notes: "",
  };
}

function lineFromPurchaseOrder(poLine: PurchaseOrderLine): LineForm {
  return {
    lineKey: poLine.id,
    drugId: poLine.drugId,
    orderedQty: poLine.orderedQty,
    batchNo: "",
    expiryDate: "",
    quantity: poLine.orderedQty,
    mrp: 0,
    purchaseRate: 0,
    gstRate: poLine.drug?.gstRate ?? null,
    discountRate: null,
    notes: poLine.notes ?? "",
  };
}

function poOrderedQty(selected: PharmacyPurchaseOrder, drugId: string): number | undefined {
  return selected.lines.find((l) => l.drugId === drugId)?.orderedQty;
}

export default function PharmaVendorPortalPage() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [header, setHeader] = useState({
    invoiceNumber: "",
    invoiceDate: format(new Date(), "yyyy-MM-dd"),
    totalAmount: "",
    notes: "",
  });
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);

  const { data: profile } = useQuery({
    queryKey: ["pharma-vendor-me"],
    queryFn: async () => (await pharmaVendorPortalApi.me()).data?.data,
  });

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["pharma-vendor-orders"],
    queryFn: async () => {
      const res = await pharmaVendorPortalApi.listOrders();
      return (res.data?.data ?? []) as PharmacyPurchaseOrder[];
    },
  });

  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) ?? null,
    [orders, selectedId]
  );

  const { data: drugs = [] } = useQuery({
    queryKey: ["prescription-drugs"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  useEffect(() => {
    if (!selected) return;
    const d = selected.delivery;
    if (d) {
      setHeader({
        invoiceNumber: d.invoiceNumber,
        invoiceDate: d.invoiceDate.slice(0, 10),
        totalAmount: String(d.totalAmount),
        notes: d.notes ?? "",
      });
      setLines(
        d.lines.length
          ? d.lines.map((l) => ({
              lineKey: l.id,
              drugId: l.drugId,
              orderedQty: poOrderedQty(selected, l.drugId),
              batchNo: l.batchNo,
              expiryDate: l.expiryDate.slice(0, 10),
              quantity: l.quantity,
              mrp: l.mrp,
              purchaseRate: l.purchaseRate,
              gstRate: l.gstRate,
              discountRate: l.discountRate,
              notes: l.notes ?? "",
            }))
          : selected.lines.map((l) => lineFromPurchaseOrder(l))
      );
    } else {
      setHeader({
        invoiceNumber: "",
        invoiceDate: format(new Date(), "yyyy-MM-dd"),
        totalAmount: "",
        notes: "",
      });
      setLines(
        selected.lines.length
          ? selected.lines.map((l) => lineFromPurchaseOrder(l))
          : [emptyLine()]
      );
    }
    setInvoiceFile(null);
  }, [selected]);

  const uploadMut = useMutation({
    mutationFn: ({ orderId, file }: { orderId: string; file: File }) =>
      pharmaVendorPortalApi.uploadInvoiceFile(orderId, file),
    onSuccess: () => {
      toast.success("Invoice file uploaded");
      qc.invalidateQueries({ queryKey: ["pharma-vendor-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Upload failed"),
  });

  const deliveryLinesPayload = useMemo(
    () =>
      lines
        .filter((l) => l.drugId && l.batchNo?.trim())
        .map(({ lineKey: _k, orderedQty: _oq, ...rest }) => rest),
    [lines]
  );

  const saveMut = useMutation({
    mutationFn: ({ orderId, submit }: { orderId: string; submit: boolean }) =>
      pharmaVendorPortalApi.saveDelivery(orderId, {
        invoiceNumber: header.invoiceNumber.trim(),
        invoiceDate: header.invoiceDate,
        totalAmount: header.totalAmount ? Number(header.totalAmount) : undefined,
        notes: header.notes || undefined,
        submit,
        lines: deliveryLinesPayload,
      }),
    onSuccess: (_d, vars) => {
      toast.success(vars.submit ? "Delivery submitted to hospital" : "Draft saved");
      qc.invalidateQueries({ queryKey: ["pharma-vendor-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Save failed"),
  });

  const canEdit =
    selected &&
    (selected.status === "SENT" || selected.status === "REJECTED" || selected.status === "VENDOR_SUBMITTED");

  const handleSubmit = async (submit: boolean) => {
    if (!selected) return;
    if (!header.invoiceNumber.trim()) {
      toast.error("Invoice number is required");
      return;
    }
    if (!deliveryLinesPayload.length) {
      toast.error("Add at least one line with batch number, expiry, quantity, and rates");
      return;
    }
    try {
      await saveMut.mutateAsync({ orderId: selected.id, submit: false });
      let hasInvoiceFile = Boolean(selected.delivery?.fileUrl);
      if (invoiceFile) {
        await uploadMut.mutateAsync({ orderId: selected.id, file: invoiceFile });
        setInvoiceFile(null);
        hasInvoiceFile = true;
      }
      if (submit) {
        if (!hasInvoiceFile) {
          toast.error("Upload the invoice PDF or image before submitting to the hospital");
          return;
        }
        await saveMut.mutateAsync({ orderId: selected.id, submit: true });
      }
    } catch {
      /* toast from mutation onError */
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold">Pharmacy supplier portal</h1>
        <p className="text-sm text-muted-foreground">
          {profile?.name} · {profile?.hospital?.name}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Purchase orders</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {isLoading ? (
              <Loader2 className="mx-auto h-6 w-6 animate-spin" />
            ) : orders.length === 0 ? (
              <p className="text-sm text-muted-foreground">No orders yet.</p>
            ) : (
              orders.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setSelectedId(o.id)}
                  className={`w-full rounded-lg border p-3 text-left hover:bg-muted/50 ${
                    selectedId === o.id ? "border-primary bg-muted/40" : ""
                  }`}
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{o.orderNumber}</span>
                    <Badge variant="outline" className="text-[10px] font-normal max-w-[55%] text-right leading-tight">
                      {vendorPoStatusLabel(o.status)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {format(new Date(o.createdAt), "dd MMM yyyy")} · {o.lines.length} item(s)
                  </p>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 overflow-hidden">
          <CardHeader className="border-b bg-muted/30 pb-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Invoice &amp; delivery details</CardTitle>
                {selected ? (
                  <p className="text-xs text-muted-foreground mt-1">
                    PO {selected.orderNumber} · {selected.lines.length} medicine
                    {selected.lines.length === 1 ? "" : "s"}
                  </p>
                ) : null}
              </div>
              {selected ? (
                <Badge variant="secondary" className="font-normal max-w-[12rem] text-right leading-tight">
                  {vendorPoStatusLabel(selected.status)}
                </Badge>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {!selected ? (
              <p className="text-sm text-muted-foreground p-6">Select an order to fill invoice details.</p>
            ) : !canEdit ? (
              <p className="text-sm text-muted-foreground p-6">
                This order is closed ({vendorPoStatusLabel(selected.status).toLowerCase()}).
              </p>
            ) : (
              <div className="divide-y">
                {selected.status === "REJECTED" && selected.rejectionReason && (
                  <p className="text-sm text-red-700 border-b border-red-200 bg-red-50 px-4 py-3">
                    Hospital rejected: {selected.rejectionReason}. Please correct and resubmit.
                  </p>
                )}

                {selected.notes ? (
                  <div className="px-4 py-3 text-sm bg-amber-50/80 border-b border-amber-100 text-amber-950">
                    <span className="font-medium">Hospital PO note: </span>
                    {selected.notes}
                  </div>
                ) : null}

                <section className="p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <FileText className="h-4 w-4 text-primary" />
                    Invoice details
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="inv-number">Invoice number</Label>
                      <Input
                        id="inv-number"
                        value={header.invoiceNumber}
                        onChange={(e) => setHeader((h) => ({ ...h, invoiceNumber: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="inv-date">Invoice date</Label>
                      <Input
                        id="inv-date"
                        type="date"
                        value={header.invoiceDate}
                        onChange={(e) => setHeader((h) => ({ ...h, invoiceDate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="inv-total">Invoice total (₹)</Label>
                      <Input
                        id="inv-total"
                        type="number"
                        value={header.totalAmount}
                        onChange={(e) => setHeader((h) => ({ ...h, totalAmount: e.target.value }))}
                        placeholder="Optional — calculated from lines"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="inv-file">Invoice file (PDF or image)</Label>
                      <Input
                        id="inv-file"
                        type="file"
                        accept=".pdf,image/*"
                        className="cursor-pointer file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1 file:text-sm file:font-medium file:text-primary"
                        onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
                      />
                      {invoiceFile ? (
                        <p className="text-xs text-muted-foreground truncate">Selected: {invoiceFile.name}</p>
                      ) : selected.delivery?.fileUrl ? (
                        <a
                          href={selected.delivery.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-primary hover:underline inline-block"
                        >
                          View uploaded invoice
                        </a>
                      ) : (
                        <p className="text-xs text-muted-foreground">Required before final submit</p>
                      )}
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="inv-notes">Your notes (optional)</Label>
                      <Textarea
                        id="inv-notes"
                        rows={2}
                        className="resize-none"
                        value={header.notes}
                        onChange={(e) => setHeader((h) => ({ ...h, notes: e.target.value }))}
                        placeholder="Delivery remarks for the hospital pharmacist"
                      />
                    </div>
                  </div>
                </section>

                <Separator />

                <section className="p-4 sm:p-5 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Package className="h-4 w-4 text-primary" />
                      Medicines delivered
                    </div>
                    <span className="text-xs text-muted-foreground">{lines.length} line(s)</span>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Enter batch, expiry, delivered quantity, and rates for each item on the purchase order.
                  </p>

                  <div className="space-y-4">
                    {lines.map((line, idx) => {
                      const poLine = selected.lines.find((l) => l.drugId === line.drugId);
                      const drugDisplay = poLine?.drug
                        ? drugLabel(poLine.drug as Drug)
                        : drugs.find((d) => d.id === line.drugId);
                      const drugName =
                        typeof drugDisplay === "string"
                          ? drugDisplay
                          : drugLabel(drugDisplay ?? null);

                      const patchLine = (patch: Partial<LineForm>) => {
                        setLines((p) => {
                          const n = [...p];
                          n[idx] = { ...n[idx], ...patch };
                          return n;
                        });
                      };

                      return (
                        <div
                          key={line.lineKey}
                          className="rounded-lg border bg-background shadow-sm overflow-hidden"
                        >
                          <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2.5">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background text-xs font-semibold text-muted-foreground border">
                              {idx + 1}
                            </span>
                            {line.orderedQty != null ? (
                              <>
                                <p className="min-w-0 flex-1 text-sm font-medium leading-snug">{drugName}</p>
                                <Badge variant="outline" className="tabular-nums shrink-0">
                                  PO qty {line.orderedQty}
                                  {poLine?.drug?.unit ? ` ${poLine.drug.unit}` : ""}
                                </Badge>
                              </>
                            ) : (
                              <div className="min-w-0 flex-1">
                                <Label className="text-xs text-muted-foreground mb-1 block">Medicine</Label>
                                <Select
                                  value={line.drugId}
                                  onValueChange={(v) => {
                                    const drug = drugs.find((d) => d.id === v);
                                    patchLine({ drugId: v, gstRate: drug?.gstRate ?? null });
                                  }}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Select drug" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drugs.map((d) => (
                                      <SelectItem key={d.id} value={d.id}>
                                        {drugLabel(d)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>

                          {poLine?.notes ? (
                            <p className="text-xs text-muted-foreground px-3 py-2 border-b bg-muted/20">
                              Hospital line note: {poLine.notes}
                            </p>
                          ) : null}

                          <div className="p-3 sm:p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Batch number</Label>
                              <Input
                                value={line.batchNo}
                                onChange={(e) => patchLine({ batchNo: e.target.value })}
                                placeholder="e.g. G251020087"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">Expiry date</Label>
                              <Input
                                type="date"
                                value={line.expiryDate}
                                onChange={(e) => patchLine({ expiryDate: e.target.value })}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">Delivered quantity</Label>
                              <Input
                                type="number"
                                min={1}
                                value={line.quantity || ""}
                                onChange={(e) => patchLine({ quantity: Number(e.target.value) })}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">MRP (₹)</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={line.mrp || ""}
                                onChange={(e) => patchLine({ mrp: Number(e.target.value) })}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">Purchase rate (₹)</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={line.purchaseRate || ""}
                                onChange={(e) => patchLine({ purchaseRate: Number(e.target.value) })}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">GST %</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={line.gstRate ?? ""}
                                onChange={(e) =>
                                  patchLine({
                                    gstRate: e.target.value === "" ? null : Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() => setLines((p) => [...p, emptyLine()])}
                  >
                    Add extra medicine line
                  </Button>
                </section>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-5 bg-muted/30 border-t">
                  <Button
                    variant="outline"
                    disabled={saveMut.isPending || uploadMut.isPending}
                    onClick={() => handleSubmit(false)}
                  >
                    Save draft
                  </Button>
                  <Button
                    disabled={saveMut.isPending || uploadMut.isPending}
                    onClick={() => handleSubmit(true)}
                  >
                    {saveMut.isPending || uploadMut.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" /> Submit to hospital
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
